// استخدام العميل لضمان تفاعل المكون في المتصفح
"use client"

// استيراد الأدوات والمكوّنات الداعمة لشريط التنقل
import { useEffect, useMemo, useState } from "react"
import { Button } from "@/components/ui/button"
import { Menu, ArrowUpRight, X } from "lucide-react"
import { AnimatePresence, motion } from "framer-motion"
import { allCourses } from "@/pages/courses/data/courses"
import styles from "./header.module.css"

// خريطة الفئات تحوّل الدورات حسب التصنيف
type CategoryMap = Record<string, { id: string; title: string; description: string }[]>

export function Header() {
  // تجهيز الدورات بحسب التصنيف باستخدام useMemo لتحسين الأداء
  const coursesByCategory = useMemo<CategoryMap>(() => {
    return allCourses.reduce<CategoryMap>((acc, course) => {
      if (!acc[course.category]) acc[course.category] = []
      acc[course.category].push({ id: course.id, title: course.title, description: course.description })
      return acc
    }, {})
  }, [])

  // استخراج أسماء التصنيفات لإعادة استخدامها في القوائم
  const categoryNames = useMemo(() => Object.keys(coursesByCategory), [coursesByCategory])
  // حالة التحكم بظهور قائمة الدورات المنسدلة
  const [isCoursesOpen, setIsCoursesOpen] = useState(false)
  // تحديد التصنيف النشط لعرض دوراته
  const [activeCategory, setActiveCategory] = useState<string>(categoryNames[0] ?? "")
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)

  const mainNavLinks = useMemo(
    () => [
      { href: "/", label: "Home" },
      { href: "/courses", label: "Courses" },
      { href: "/reviews", label: "Reviews" },
      { href: "/pricing", label: "Pricing" },
      { href: "/blog", label: "Blog" },
      { href: "/about", label: "About" },
      { href: "/contact", label: "Contact" },
    ],
    []
  )

  // متابعة التغييرات في التصنيفات وتحديث التصنيف النشط عند الحاجة
  useEffect(() => {
    if (categoryNames.length === 0) {
      setActiveCategory("")
      return
    }
    if (!categoryNames.includes(activeCategory)) {
      setActiveCategory(categoryNames[0])
    }
  }, [categoryNames, activeCategory])

  useEffect(() => {
    if (!isMobileMenuOpen) return
    const originalOverflow = document.body.style.overflow
    document.body.style.overflow = "hidden"
    return () => {
      document.body.style.overflow = originalOverflow
    }
  }, [isMobileMenuOpen])

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setIsMobileMenuOpen(false)
      }
    }
    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [])

  // فتح القائمة المنسدلة عند التمرير أو التركيز
  const handleOpenCourses = () => {
    if (!isCoursesOpen) setIsCoursesOpen(true)
  }

  // إغلاق القائمة المنسدلة عند مغادرة المؤشر
  const handleCloseCourses = () => {
    setIsCoursesOpen(false)
  }

  // الدورات المرئية الخاصة بالتصنيف المختار
  const visibleCourses = activeCategory ? coursesByCategory[activeCategory] ?? [] : []
  const closeMobileMenu = () => setIsMobileMenuOpen(false)

  return (
    // الغلاف العام للشريط العلوي
    <div className={styles.wrapper}>
      {/* القشرة الزجاجية للشريط */}
      <header className={styles.shell}>
        {/* الحاوية الداخلية التي تجمع الشعار والقوائم والأزرار */}
        <div className={styles.headerInner} onMouseLeave={handleCloseCourses}>
          {/* رابط الشعار مع الاسم والعبارة التعريفية */}
          <a href="/" className={styles.logoLink}>
            <div>
              <img src="/logo/SVG/Asset 3.svg" alt="Dhikr Institute" className={styles.logoImage} />
            </div>
            <div className={styles.brand}>
               <span className={styles.brandName}>Dhikr Institute</span>
               <p className={styles.brandTagline}>Islamic Learning Excellence</p>
             </div>
          </a>

          {/* قائمة التنقل الرئيسية لسطح المكتب */}
          <nav className={styles.desktopNav}>
            <a href="/" className={styles.navLink}>
              Home
            </a>
            <div
              className={styles.coursesWrapper}
              onMouseEnter={() => {
                handleOpenCourses()
                if (!activeCategory && categoryNames.length > 0) setActiveCategory(categoryNames[0])
              }}
              onMouseLeave={handleCloseCourses}
              onFocus={() => {
                handleOpenCourses()
                if (!activeCategory && categoryNames.length > 0) setActiveCategory(categoryNames[0])
              }}
              onBlur={(event) => {
                if (!event.currentTarget.contains(event.relatedTarget as Node)) {
                  handleCloseCourses()
                }
              }}
            >
              {/* رابط صفحة الدورات مع تفعيل الحالة عند الفتح */}
              <a
                href="/courses"
                className={`${styles.navLink} ${isCoursesOpen ? styles.navLinkActive : ""}`}
              >
                Courses
              </a>
              {/* قائمة منسدلة للدورات مع انتقالات framer-motion */}
              <AnimatePresence>
                {isCoursesOpen && categoryNames.length > 0 && (
                  <motion.div
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 12 }}
                    transition={{ duration: 0.2, ease: [0.23, 0.68, 0.32, 0.97] }}
                    className={styles.dropdownContainer}
                    onMouseEnter={handleOpenCourses}
                    onMouseLeave={handleCloseCourses}
                  >
                    {/* لوحة الدورات التي تضم الفئات والقائمة المختارة */}
                    <div className={styles.dropdownPanel}>
                      <div className={styles.dropdownGrid}>
                        <div>
                          {/* عمود الفئات لتغيير التصنيف النشط */}
                          <p className={styles.categoryColumnTitle}>Categories</p>
                          <div className={styles.categoryList}>
                            {categoryNames.map((category) => {
                              const isActive = category === activeCategory
                              return (
                                // زر يغيّر التصنيف المعروض عند التمرير أو التركيز
                                <button
                                  key={category}
                                  onMouseEnter={() => setActiveCategory(category)}
                                  onFocus={() => setActiveCategory(category)}
                                  className={`${styles.categoryButton} ${isActive ? styles.categoryButtonActive : ""}`}
                                >
                                  {category}
                                </button>
                              )
                            })}
                          </div>
                        </div>

                        <div>
                          {/* رأس عمود الدورات المختارة مع رابط عرض الكل */}
                          <div className={styles.coursesColumnHeader}>
                            <p className={styles.coursesColumnTitle}>Featured Courses</p>
                            <a href="/courses" className={styles.viewAll}>
                              View all
                              <ArrowUpRight className="h-3.5 w-3.5" />
                            </a>
                          </div>
                          {/* قائمة الدورات المختارة حسب التصنيف */}
                          <div className={styles.courseList}>
                            {visibleCourses.slice(0, 4).map((course) => (
                              // بطاقة مختصرة للدورة مع العنوان والوصف
                              <a
                                key={course.id}
                                href={`/courses/${course.id}`}
                                className={styles.courseItem}
                              >
                                <div className={styles.courseIndicator} />
                                <div className={styles.courseText}>
                                  <p className={styles.courseTitle}>{course.title}</p>
                                  <p className={styles.courseDescription}>{course.description}</p>
                                </div>
                              </a>
                            ))}
                            {visibleCourses.length === 0 && (
                              // حالة فارغة عند عدم توفر دورات في التصنيف
                              <div className={styles.emptyState}>
                                Courses coming soon.
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
            <a href="/reviews" className={styles.navLink}>
              Reviews
            </a>
            <a href="/pricing" className={styles.navLink}>
              Pricing
            </a>
            <a href="/blog" className={styles.navLink}>
              Blog
            </a>
            <a href="/about" className={styles.navLink}>
              About
            </a>
            <a href="/contact" className={styles.navLink}>
              Contact
            </a>
          </nav>

          {/* مجموعة الأزرار: تجربة مجانية، زر القائمة */}
          <div className={styles.ctaGroup}>
            {/* زر تجربة مجانية */}
            <a href="/free-trial" className={styles.trialButton}>
              Free Trial
            </a>
            {/* زر القائمة المنسدلة */}
            <Button
              variant="ghost"
              size="icon"
              className={styles.menuButton}
              onClick={() => setIsMobileMenuOpen(true)}
              aria-label="Open navigation menu"
              aria-expanded={isMobileMenuOpen}
              aria-controls="mobile-menu"
            >
              <Menu className={styles.menuIcon} />
            </Button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            className={styles.mobileMenuBackdrop}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              id="mobile-menu"
              className={styles.mobileMenuPanel}
              initial={{ y: "-8%", opacity: 0 }}
              animate={{ y: "0%", opacity: 1 }}
              exit={{ y: "-8%", opacity: 0 }}
              transition={{ duration: 0.18, ease: [0.23, 0.68, 0.32, 0.97] }}
            >
              <div className={styles.mobileMenuHeader}>
                <a href="/" className={styles.mobileLogo} onClick={closeMobileMenu}>
                  <img src="/logo/SVG/Asset 3.svg" alt="Dhikr Institute" />
                  <div>
                    <span>Dhikr Institute</span>
                    <small>Islamic Learning Excellence</small>
                  </div>
                </a>
                <button
                  type="button"
                  className={styles.mobileCloseButton}
                  onClick={closeMobileMenu}
                  aria-label="Close navigation menu"
                >
                  <X className={styles.mobileCloseIcon} />
                </button>
              </div>

              <div className={styles.mobileMenuBody}>
                <nav className={styles.mobileMenuLinks}>
                  {mainNavLinks.map((link) => (
                    <a key={link.href} href={link.href} className={styles.mobileMenuLink} onClick={closeMobileMenu}>
                      {link.label}
                    </a>
                  ))}
                </nav>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
